import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card, useCategoryLabel, CHART_COLORS } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis } from "recharts";
import { Trophy, MapPin } from "lucide-react";

const STATES = ["Delhi", "Uttar Pradesh", "Maharashtra", "Bihar", "Rajasthan"];

export default function AdminAnalytics() {
  const { t } = useLang();
  const catLabel = useCategoryLabel();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [state, setState] = useState("Delhi");
  const [stateData, setStateData] = useState(null);

  useEffect(() => {
    api.get("/analytics/admin").then((r) => setData(r.data));
    api.get("/analytics/leaderboard").then((r) => setBoard(r.data));
  }, []);
  useEffect(() => { api.get(`/analytics/statewise/${encodeURIComponent(state)}`).then((r) => setStateData(r.data)); }, [state]);

  const pie = (data?.category_distribution || []).map((c) => ({ name: catLabel(c.category_code), value: c.count }));
  const statePie = (stateData?.category_distribution || []).map((c) => ({ name: catLabel(c.category_code), value: c.count }));

  return (
    <div>
      <PageHeader title={t("analytics")} subtitle="Admin insights & drill-down" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <h3 className="font-head font-semibold text-slate-900 mb-4">{t("categoryDistribution")}</h3>
          {pie.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {pie.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm">{t("noData")}</p>}
        </Card>
        <Card>
          <h3 className="font-head font-semibold text-slate-900 mb-4">{t("topMistakes")}</h3>
          {data?.top_mistakes?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.top_mistakes} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="mistake" width={160} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#DC2626" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm">{t("noData")}</p>}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><MapPin className="h-4 w-4 text-purple-600" /> {t("stateDrilldown")}</h3>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger className="w-40" data-testid="state-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {statePie.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statePie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {statePie.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm">{t("noData")} — {state}</p>}
        </Card>
        <Card>
          <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> {t("leaderboard")}</h3>
          <div className="space-y-2">
            {board.slice(0, 10).map((b, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                <div className="flex items-center gap-2">
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
                  <span className="text-slate-700">{b.name} <span className="text-slate-400 text-xs">· {b.state}</span></span>
                </div>
                <span className="font-medium text-slate-900">{b.avg}% <span className="text-slate-400 text-xs">({b.sessions})</span></span>
              </div>
            ))}
            {!board.length && <p className="text-slate-400 text-sm">{t("noData")}</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
